/**
 * FlagLifecycle — shared UI bits for the coaching-flag lifecycle (CP3).
 *
 *   <FlagStatusBadge />       — the status chip (label + tone per state).
 *   <FlagTransitionSelect />  — a compact "Move to…" select offering ONLY the
 *                               legal manager transitions from the current
 *                               status (forward skips + the closed→reopen).
 *
 * The select is used by the ManagerView coaching inbox and the AgentProfile
 * open-items table (both pass the row's flag id to the PATCH so the exact row
 * moves); CallDetail's coaching card renders only the badge — the rep's one
 * action there is the dedicated Acknowledge button. The server
 * (PATCH /api/calls/:id/flag) is the authority on legality — an illegal pick
 * returns a 409 the caller surfaces as a friendly message.
 */
import type { FlagLifecycleStatus } from "@/types";
import { Badge } from "@/components/ui";
import {
  FLAG_STATUS_LABELS,
  FLAG_STATUS_TONES,
  legalTransitionTargets,
} from "@/lib/coachingLifecycle";

export function FlagStatusBadge({ status }: { status: FlagLifecycleStatus }) {
  return <Badge tone={FLAG_STATUS_TONES[status]}>{FLAG_STATUS_LABELS[status]}</Badge>;
}

/**
 * Compact transition picker. Always shows the placeholder ("Move to…") so each
 * pick is an explicit action; resets itself after firing. `pending` disables it
 * and swaps the placeholder while the PATCH is in flight.
 */
export function FlagTransitionSelect({
  status,
  onTransition,
  pending = false,
}: {
  status: FlagLifecycleStatus;
  onTransition: (to: FlagLifecycleStatus) => void;
  pending?: boolean;
}) {
  const targets = legalTransitionTargets(status);
  const isDisabled = pending || targets.length === 0;
  return (
    <select
      value=""
      disabled={isDisabled}
      aria-label="Move this coaching item to a new status"
      onChange={(e) => {
        const to = e.target.value as FlagLifecycleStatus | "";
        if (to) onTransition(to);
      }}
      className="rounded-md border bg-background px-2 py-1 text-xs outline-none focus:ring-2 focus:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
    >
      <option value="" disabled>
        {pending ? "Saving…" : "Move to…"}
      </option>
      {targets.map((t) => (
        <option key={t} value={t}>
          {t === "needs_review" && status === "closed"
            ? "Reopen (needs review)"
            : FLAG_STATUS_LABELS[t]}
        </option>
      ))}
    </select>
  );
}
